import type { AccessibleFleet } from '@cobliteam/common-types';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef.type';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { cn } from '@cobliteam/ui/utils';
import type React from 'react';
import { useEffect, useState } from 'react';
import AppFleetSwitcher from '../AppFleetSwitcher';
import BoletosButton from '../AppHeader/components/BoletosButton';
import Incidents from '../AppHeader/components/Incidents';
import NewsFeed from '../AppHeader/components/NewsFeed';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarSeparator, useSidebar } from './components/Sidebar';
import { SidebarMenu } from './components/SidebarMenu';
import { SidebarMenuMobile } from './components/SidebarMenuMobile';
import { useRailDensity } from './hooks/use-rail-density';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  fleets: AccessibleFleet[];
  currentFleet: AccessibleFleet;
}

function AppSidebar({ fleets, currentFleet, ...props }: AppSidebarProps) {
  const { sidebar } = useDashboard();
  const { openMobile, isCollapsed, isMobile } = useSidebar();
  const [primaryHovered, setPrimaryHovered] = useState<RouteDef | null>(null);
  // primary sidebar
  const primarySidebar = sidebar.router.filter((route) => route.sidebar?.hide !== true);
  const mainSidebar = primarySidebar.slice(0, primarySidebar.length - 1); // remove last two items
  const footerSidebar = primarySidebar.slice(primarySidebar.length - 1); // last two items
  const hasSecondarySidebar = !!primaryHovered?.routes && !!(primaryHovered.routes.length > 0) && primaryHovered.routes.some((route) => route.sidebar?.hide !== true);
  const railDensity = useRailDensity(mainSidebar.length);

  useEffect(() => {
    const hoveredPrimary = isCollapsed ? null : sidebar.primary;
    setPrimaryHovered(hoveredPrimary || null);
  }, [isCollapsed, sidebar.primary]);

  const handleMouseEnter = (route: RouteDef | null) => {
    setPrimaryHovered(route);
  };

  if (!primarySidebar) return null;

  if (isMobile) {
    return (
      <Sidebar variant='sidebar' {...props}>
        <SidebarHeader className='relative min-h-13 pr-13'>
          <AppFleetSwitcher fleets={fleets} currentFleet={currentFleet} />
        </SidebarHeader>
        <SidebarContent
          className={cn(
            'min-h-0 flex-1 overscroll-contain',
            'scrollbar',
            'scrollbar-thumb-custom',
            'scrollbar-thin',
            'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
            'scrollbar-thumb-gray-11',
            'scrollbar-thumb-bg-clip-content',
            'scrollbar-track-gray-12'
          )}
        >
          <SidebarGroup className='shrink-0 overflow-visible'>
            <SidebarGroupContent className='space-y-0 overflow-visible'>
              <SidebarMenuMobile />
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    );
  }

  return (
    <>
      {/* Primary sidebar */}
      <Sidebar
        collapsible={isCollapsed ? 'none' : 'icon'}
        onMouseLeave={() => setPrimaryHovered(isCollapsed ? null : sidebar.primary || null)}
        hasSecondary={hasSecondarySidebar}
        variant='sidebar'
        className={cn('flex overflow-hidden [&>[data-sidebar=sidebar]]:flex-row', isCollapsed && '!w-(--sidebar-width-icon)')}
        {...props}
      >
        <Sidebar id='primary-sidebar' collapsible='none' className={cn('!w-(--sidebar-width-icon) h-screen bg-gray-1')}>
          {/* Header */}
          <SidebarHeader>
            <AppFleetSwitcher fleets={fleets} currentFleet={currentFleet} />
          </SidebarHeader>
          <SidebarSeparator className={cn('my-2', openMobile && 'mb-0')} />

          {/* Main */}
          <SidebarContent className='h-[calc(100vh-144px)]'>
            <SidebarGroup>
              <SidebarGroupContent className={cn('mt-2', railDensity === 'full' ? 'space-y-4' : 'space-y-2')}>
                {!openMobile && <SidebarMenu primary density={railDensity} primaryDef={mainSidebar} onMouseEnter={handleMouseEnter} hoveredRoute={primaryHovered} />}
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          {/* Footer */}
          <SidebarFooter>
            {!openMobile && (
              <SidebarGroup className='overflow-visible'>
                <SidebarGroupContent className='flex flex-col items-center justify-center overflow-visible pb-2.5'>
                  <Incidents />
                  <BoletosButton />
                  <NewsFeed />
                  <SidebarMenu primary showTitle={false} primaryDef={footerSidebar} onMouseEnter={setPrimaryHovered} hoveredRoute={primaryHovered} />
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarFooter>
        </Sidebar>

        {/* Secondary sidebar */}
        <Sidebar
          id='secondary-sidebar'
          collapsible='none'
          className={cn(
            'left-0 w-(--sidebar-width-secondary) overflow-hidden border-none',
            !hasSecondarySidebar && 'hidden',
            isCollapsed && '!w-(--sidebar-width-secondary) absolute top-0 left-[74px] z-[1001] h-screen border-solid shadow-lg'
          )}
        >
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu primaryDef={primarySidebar} primaryHovered={primaryHovered || undefined} />
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      </Sidebar>
    </>
  );
}

export default AppSidebar;
